import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Dna, Compass, Activity, FlaskConical, ShieldCheck, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BrandMark } from "@/components/AppShell";
import Footer from "@/components/Footer";

const LOOP = [
  { label: "Understand", desc: "Signals from biomarkers, lifestyle and (optionally) genomic reports." },
  { label: "Prioritise", desc: "Top 5 opportunities ranked by Impact × Modifiability × Evidence × Actionability × Readiness." },
  { label: "Intervene", desc: "Each opportunity ships with a baseline, objective and weekly actions." },
  { label: "Measure", desc: "Check-ins track adherence + biomarker shifts; outcomes attribute to interventions." },
  { label: "Learn", desc: "The journey adapts — modify, escalate or declare win based on what moved." },
];

const MODES = [
  {
    icon: Dna, title: "Genomics-first",
    body: "You already have a genetic screening report (23andMe, Illumina, targeted panel). Upload it — we land it in counsellor review and still build your prevention map from your current labs.",
  },
  {
    icon: Activity, title: "Health-first",
    body: "No genomic test yet. Share current biomarkers, family history and lifestyle. We'll flag whether genomic testing would meaningfully add — without pushing unnecessary tests.",
  },
  {
    icon: FlaskConical, title: "Nutrition-first",
    body: "Start with your plate. Food logs + a biomarker check (fasting glucose, lipids) light up a nutrition-only pathway, no genomics required.",
  },
];

export default function GenomeLanding() {
  return (
    <div className="min-h-screen bg-background" data-testid="genome-landing">
      <header className="border-b border-border bg-white/85 backdrop-blur">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" aria-label="Arogya Pramana"><BrandMark size={48} /></Link>
          <nav className="hidden md:flex items-center gap-6 text-sm text-muted-foreground">
            <Link to="/directory" className="hover:text-primary">Directory</Link>
            <Link to="/hcahps" className="hover:text-primary">HCAHPS</Link>
            <Link to="/genome" className="text-primary font-medium">GenomePramana</Link>
            <Link to="/login" className="hover:text-primary">Sign in</Link>
          </nav>
        </div>
      </header>

      <section className="border-b border-border bg-gradient-to-b from-white to-background">
        <div className="max-w-6xl mx-auto px-6 py-16 grid md:grid-cols-5 gap-10 items-center">
          <div className="md:col-span-3">
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-3 py-1 text-[11px] uppercase tracking-widest text-muted-foreground">
              <Sparkles size={11} className="text-accent" /> GenomePramana · Preview release
            </div>
            <h1 className="mt-5 font-serif text-4xl sm:text-5xl text-primary leading-tight">
              Know your risk. Understand what you can change.<br />
              <span className="text-accent">Know what to do next.</span>
            </h1>
            <p className="mt-5 max-w-2xl text-base text-muted-foreground leading-relaxed">
              GenomePramana is a precision-prevention platform — not a report portal. Share what we need, we rank your top health opportunities, you follow a measurable plan, we track whether it moved the number.
            </p>
            <div className="mt-7 flex items-center gap-3">
              <Link to="/genome/start">
                <Button className="h-11 px-5" data-testid="genome-start-cta">Start my journey <ArrowRight size={16} className="ml-2" /></Button>
              </Link>
              <a href="#how" className="text-sm text-primary underline">How it works</a>
            </div>
            <div className="mt-7 flex flex-wrap gap-x-6 gap-y-2 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1"><ShieldCheck size={12} className="text-primary" /> Evidence-graded recommendations</span>
              <span className="inline-flex items-center gap-1"><ShieldCheck size={12} className="text-primary" /> Consent-gated · DPDP-ready</span>
              <span className="inline-flex items-center gap-1"><ShieldCheck size={12} className="text-primary" /> Counsellor review on all uploads</span>
            </div>
          </div>
          <div className="md:col-span-2">
            <div className="rounded-lg border border-border bg-white p-5 shadow-sm">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground">The loop</div>
              <ol className="mt-3 space-y-3">
                {LOOP.map((step, i) => (
                  <li key={step.label} className="flex items-start gap-3">
                    <div className="shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center">{i + 1}</div>
                    <div>
                      <div className="text-sm font-medium text-primary">{step.label}</div>
                      <div className="text-xs text-muted-foreground">{step.desc}</div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </section>

      <section id="how" className="max-w-6xl mx-auto px-6 py-14">
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Three ways to start</div>
        <h2 className="mt-2 font-serif text-3xl text-primary">Pick your doorway</h2>
        <div className="mt-6 grid gap-4 md:grid-cols-3">
          {MODES.map((m) => (
            <div key={m.title} className="rounded-lg border border-border bg-white p-5 hover:border-primary/40 transition-colors">
              <div className="w-10 h-10 rounded-md bg-primary/10 text-primary flex items-center justify-center"><m.icon size={18} /></div>
              <div className="mt-3 font-serif text-lg text-primary">{m.title}</div>
              <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{m.body}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 rounded-lg border border-primary/20 bg-primary/5 p-5">
          <div className="flex items-start gap-3">
            <Compass size={18} className="text-primary mt-0.5" />
            <div>
              <div className="font-serif text-primary text-lg">Pilot vertical · Cardiometabolic</div>
              <p className="mt-1 text-sm text-muted-foreground">V1 ranks 8 opportunities across metabolic, cardio, activity, nutrition, recovery and screening — tuned for South Asian risk profiles. Hereditary cancer, pharmacogenomics, carrier screening and polygenic risk ship in subsequent phases.</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
